import {
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
  MenuItemMeta,
  MenuSectionLabel,
  SubmenuCaret,
} from '@src/components/common/contextMenuStyles';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import {
  isOverflowSubmenu,
  useTranscriptOverflowExtraSections,
} from '@src/components/transcript/TranscriptOverflowExtras';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type {
  TranscriptOverflowEntry,
  TranscriptOverflowItem,
  TranscriptOverflowSection,
  TranscriptOverflowSubmenu,
} from '@src/components/transcript/TranscriptOverflowExtras';
import type { CSSProperties, FC, KeyboardEvent, MouseEvent } from 'react';

export type {
  TranscriptOverflowEntry,
  TranscriptOverflowItem,
  TranscriptOverflowSection,
  TranscriptOverflowSubmenu,
} from '@src/components/transcript/TranscriptOverflowExtras';

type Props = {
  /**
   * The transcript's own actions; the menu renders nothing when empty (a
   * surface with no transcript actions — reviewer mode — offers no menu, so
   * the sections registered by header slots are not shown there either).
   */
  items: TranscriptOverflowItem[];
};

const ROW_HEIGHT = 36; // ContextMenuItem row (~34px) + 2px gap
const LABEL_HEIGHT = 24;
const SURFACE_PADDING = 12;
const SUBMENU_WIDTH = 220;
/** Daylight between the parent surface and its submenu. */
const SUBMENU_GAP = 4;
// Grace before an open submenu closes once the pointer has left both its row
// and itself, and before a sibling row's submenu replaces it. A pointer
// heading from the row towards a submenu entry lower down crosses the rows
// between them (and the surface padding) first; with a shorter grace the
// sibling it crossed took the submenu over, or the gap closed it.
const FLYOUT_CLOSE_GRACE_MS = 320;
const FLYOUT_SWITCH_GRACE_MS = 260;

const estimateMenuHeight = (rows: number, labels = 0) =>
  rows * ROW_HEIGHT + labels * LABEL_HEIGHT + SURFACE_PADDING;

type Flyout = { key: string; x: number; y: number };

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

/**
 * PLAT-10646 — trailing "More actions" kebab for the transcript header.
 * Consolidates the rare, state-dependent actions (fix transcript
 * alignment, reset / clear captions) that previously appeared and
 * disappeared as standalone buttons, reshuffling the toolbar. Follows the
 * standard context-menu pattern (see SpeakerRowActionsMenu): portaled,
 * viewport-clamped surface that closes on outside-click or Escape.
 *
 * PLAT-11979 — the menu also lists the sections registered through
 * `TranscriptOverflowExtrasProvider` (the Languages section, from
 * `TranslationToggle`). With any extra section present the transcript's own
 * actions become the "Transcript" section; a submenu row opens a second-level
 * menu beside it on hover, click or ArrowRight. See "Sections and submenus" in
 * docs/design-system.md.
 */
const TranscriptOverflowMenu: FC<Props> = ({ items }) => {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [flyout, setFlyout] = useState<Flyout | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const switchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const extraSections = useTranscriptOverflowExtraSections(pos !== null);

  const cancelFlyoutSwitch = useCallback(() => {
    if (switchTimer.current) clearTimeout(switchTimer.current);
    switchTimer.current = null;
  }, []);
  const cancelFlyoutClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  // Grace period so the pointer can travel from the row into its submenu.
  const scheduleFlyoutClose = useCallback(() => {
    cancelFlyoutClose();
    closeTimer.current = setTimeout(
      () => setFlyout(null),
      FLYOUT_CLOSE_GRACE_MS,
    );
  }, [cancelFlyoutClose]);
  // The pointer is back on the open submenu's row or inside the submenu:
  // nothing pending may close or replace it.
  const holdFlyout = useCallback(() => {
    cancelFlyoutClose();
    cancelFlyoutSwitch();
  }, [cancelFlyoutClose, cancelFlyoutSwitch]);
  const close = useCallback(() => {
    cancelFlyoutClose();
    cancelFlyoutSwitch();
    setFlyout(null);
    setPos(null);
  }, [cancelFlyoutClose, cancelFlyoutSwitch]);
  useEffect(
    () => () => {
      cancelFlyoutClose();
      cancelFlyoutSwitch();
    },
    [cancelFlyoutClose, cancelFlyoutSwitch],
  );

  // Outside-mousedown closes the menu — except on the trigger itself,
  // whose own click handler owns the toggle (otherwise the alerter
  // closes on mousedown and the click immediately re-opens).
  const handleOutside = useCallback(
    (event: globalThis.MouseEvent) => {
      if (
        triggerRef.current &&
        event.target instanceof Node &&
        triggerRef.current.contains(event.target)
      ) {
        return;
      }
      close();
    },
    [close],
  );
  useOutsideAlerter(portalRef, handleOutside);

  useEffect(() => {
    if (!pos) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (flyout) {
        setFlyout(null);
        focusRow(surfaceRef.current, `[data-submenu-key="${flyout.key}"]`);
        return;
      }
      close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, flyout, close]);

  if (items.length === 0) return null;

  const sections: TranscriptOverflowSection[] =
    extraSections.length > 0
      ? [
          { key: 'transcript', label: 'Transcript', entries: items },
          ...extraSections,
        ]
      : [];
  const rowCount = sections.length
    ? sections.reduce((n, s) => n + s.entries.length, 0)
    : items.length;

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    // Right-align the surface with the trigger so the menu drops
    // directly below the kebab (the surface auto-sizes to its content,
    // so left-edge math over- or under-shoots).
    const rect = event.currentTarget.getBoundingClientRect();
    setPos({
      top: Math.min(
        rect.bottom + 4,
        window.innerHeight - estimateMenuHeight(rowCount, sections.length) - 8,
      ),
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  const run =
    (item: TranscriptOverflowItem) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (item.disabledNote) return;
      const rowRect = event.currentTarget.getBoundingClientRect();
      if (!item.keepOpen) close();
      item.onClick(rowRect);
    };

  // Open a submenu beside its row — to the right, flipping to the left when
  // it would overflow the viewport, and clamped to the bottom edge.
  const openFlyout = (entry: TranscriptOverflowSubmenu, rowEl: HTMLElement) => {
    cancelFlyoutClose();
    cancelFlyoutSwitch();
    const r = rowEl.getBoundingClientRect();
    // Horizontally the submenu sits beside the parent SURFACE (the row ends
    // inside its padding and border, so anchoring on the row overlapped it).
    const m = surfaceRef.current?.getBoundingClientRect() ?? r;
    const subH = estimateMenuHeight(entry.items.length);
    const x =
      m.right + SUBMENU_GAP + SUBMENU_WIDTH <= window.innerWidth
        ? m.right + SUBMENU_GAP
        : m.left - SUBMENU_GAP - SUBMENU_WIDTH;
    const y = Math.max(8, Math.min(r.top - 4, window.innerHeight - subH - 8));
    setFlyout(prev =>
      prev && prev.key === entry.key && prev.x === x && prev.y === y
        ? prev
        : { key: entry.key, x, y },
    );
  };

  // Keyboard: Up/Down walk the rows of the surface the focus is in, Right
  // opens the focused submenu row and enters it, Left steps back out.
  const handleSurfaceKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const inFlyout = !!flyoutRef.current?.contains(target);
    const surface = inFlyout ? flyoutRef.current : surfaceRef.current;
    if (!surface) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const rows = Array.from(
        surface.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR),
      );
      const at = rows.indexOf(target);
      const next =
        event.key === 'ArrowDown'
          ? rows[(at + 1) % rows.length]
          : rows[(at - 1 + rows.length) % rows.length];
      next?.focus();
      return;
    }
    if (event.key === 'ArrowRight' && !inFlyout) {
      const key = target.dataset.submenuKey;
      const entry = key && findSubmenu(sections, key);
      if (!entry) return;
      event.preventDefault();
      openFlyout(entry, target);
      requestAnimationFrame(() =>
        focusRow(flyoutRef.current, MENU_ITEM_SELECTOR),
      );
      return;
    }
    if (event.key === 'ArrowLeft' && inFlyout && flyout) {
      event.preventDefault();
      const key = flyout.key;
      setFlyout(null);
      focusRow(surfaceRef.current, `[data-submenu-key="${key}"]`);
    }
  };

  // `inSubmenu` rows are the open submenu's own: hovering them must keep it
  // (the first cut scheduled the close on every row, so the submenu left
  // under the pointer about a third of a second after it was entered).
  const renderItem = (item: TranscriptOverflowItem, inSubmenu = false) => (
    <OverflowMenuItem
      key={item.key}
      role="menuitem"
      tabIndex={-1}
      $danger={item.danger}
      $disabled={!!item.disabledNote}
      aria-disabled={item.disabledNote ? true : undefined}
      data-tooltip-id={item.disabledNote ? OVERFLOW_TOOLTIP_ID : undefined}
      data-tooltip-content={item.disabledNote}
      // Above the row, as the language kebab's reasons are: below, it lands
      // on the next row and reads as part of the menu.
      data-tooltip-place="top"
      onClick={run(item)}
      onMouseEnter={() => {
        if (inSubmenu) {
          holdFlyout();
          return;
        }
        // Moving to a plain row of the parent closes an open submenu.
        if (flyout) scheduleFlyoutClose();
      }}
      data-testid={item.testId}
    >
      {item.icon}
      {item.label}
    </OverflowMenuItem>
  );

  const renderSubmenuRow = (entry: TranscriptOverflowSubmenu) => {
    const isOpen = flyout?.key === entry.key;
    return (
      <OverflowMenuItem
        key={entry.key}
        role="menuitem"
        tabIndex={-1}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-submenu-key={entry.key}
        data-testid={entry.testId}
        onMouseEnter={event => {
          // Hover intent: with another submenu open, a row the pointer is
          // only crossing on its way into that submenu must not take it over.
          // Its own submenu opens once the pointer has settled on it.
          if (flyout && flyout.key !== entry.key) {
            const rowEl = event.currentTarget;
            cancelFlyoutSwitch();
            switchTimer.current = setTimeout(
              () => openFlyout(entry, rowEl),
              FLYOUT_SWITCH_GRACE_MS,
            );
            return;
          }
          openFlyout(entry, event.currentTarget);
        }}
        onMouseLeave={() => {
          cancelFlyoutSwitch();
          scheduleFlyoutClose();
        }}
        onClick={event => {
          event.stopPropagation();
          event.preventDefault();
          openFlyout(entry, event.currentTarget);
        }}
      >
        {entry.icon}
        {entry.label}
        {entry.meta && <MenuItemMeta>{entry.meta}</MenuItemMeta>}
        <SubmenuCaret aria-hidden="true">›</SubmenuCaret>
      </OverflowMenuItem>
    );
  };

  const renderEntry = (entry: TranscriptOverflowEntry) =>
    isOverflowSubmenu(entry) ? renderSubmenuRow(entry) : renderItem(entry);

  const openSubmenu = flyout ? findSubmenu(sections, flyout.key) : null;

  return (
    <>
      <ToolbarKebabButton
        ref={triggerRef}
        tabIndex={-1}
        onClick={handleTriggerClick}
        data-testid="transcript-overflow-button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={!!pos}
        data-tooltip-id={OVERFLOW_TOOLTIP_ID}
        data-tooltip-content={pos ? undefined : 'More actions'}
      >
        <EllipsisIcon width="14" height="3" fillColor="currentColor" />
      </ToolbarKebabButton>
      {createPortal(
        <Tooltip
          id={OVERFLOW_TOOLTIP_ID}
          style={OVERFLOW_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
        />,
        document.body,
      )}
      {pos &&
        createPortal(
          <div ref={portalRef} onKeyDown={handleSurfaceKeyDown}>
            <OverflowMenuSurface
              ref={surfaceRef}
              role="menu"
              style={{ top: pos.top, right: pos.right }}
              data-testid="transcript-overflow-menu"
            >
              {sections.length === 0
                ? items.map(renderItem)
                : sections.map(section => (
                    <Fragment key={section.key}>
                      <MenuSectionLabel role="presentation">
                        {section.label}
                      </MenuSectionLabel>
                      {section.entries.map(renderEntry)}
                    </Fragment>
                  ))}
            </OverflowMenuSurface>
            {flyout && openSubmenu && (
              <OverflowMenuSurface
                ref={flyoutRef}
                role="menu"
                aria-label={openSubmenu.label}
                onMouseEnter={holdFlyout}
                onMouseLeave={scheduleFlyoutClose}
                style={{
                  top: flyout.y,
                  left: flyout.x,
                  minWidth: SUBMENU_WIDTH,
                }}
                data-testid="transcript-overflow-submenu"
              >
                {openSubmenu.items.map(item => renderItem(item, true))}
              </OverflowMenuSurface>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

const findSubmenu = (
  sections: TranscriptOverflowSection[],
  key: string,
): TranscriptOverflowSubmenu | null => {
  for (const section of sections) {
    for (const entry of section.entries) {
      if (isOverflowSubmenu(entry) && entry.key === key) return entry;
    }
  }
  return null;
};

const focusRow = (root: HTMLElement | null, selector: string) =>
  root?.querySelector<HTMLElement>(selector)?.focus();

const OVERFLOW_TOOLTIP_ID = 'transcript-overflow-tooltip';

/* Tooltips portal to <body>; the z lift keeps them visible above the
   clip-preview modal stack (~1,000,000), matching the header's other
   portaled tooltips. Reads the top-layer var so the Translation Review
   lift (PLAT-10286) still raises it further when that workspace is open.
   The fallback sits ABOVE the menu surface (PLAT-11979): a disabled row's
   reason is anchored inside the menu, and at the old 1000009 it rendered
   behind the 1000022 surface — present in the DOM, never seen. */
const OVERFLOW_TOOLTIP_STYLE: CSSProperties = {
  ...COMPACT_TOOLTIP_STYLE,
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, ${CLIP_PREVIEW_Z.transcriptOverflowMenu + 10})`,
};

/* The shared ContextMenuSurface rests at ~100k, below the clip-preview
   modal stack (~1,000,000) — inside the clip preview the menu opened
   BEHIND the modal. Lift to the transcript-toolbar dropdown band (see
   clipPreviewZ.ts); the top-layer var keeps the Translation Review lift
   working. Harmless in Creator Studio. */
const OverflowMenuSurface = styled(ContextMenuSurface)`
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.transcriptOverflowMenu}
  );
`;

/* The shared KebabButton carries a row margin for list layouts; the
   toolbar's flex gap already spaces siblings. */
const ToolbarKebabButton = styled(KebabButton)`
  margin-left: 0;
  height: 100%;
`;

/* The shared row at its standard size — the same rows the language
   dropdown's kebab shows, so the two doors to a language's actions read as
   one menu (PLAT-11979; the earlier 11px dense variant made this one look
   like a different control). Single-line labels: the surface is
   right-anchored inside the left panel, where shrink-to-fit width would
   otherwise wrap the longer entries. */
const OverflowMenuItem = styled(ContextMenuItem)`
  white-space: nowrap;
`;

export default TranscriptOverflowMenu;
